<script setup lang="ts">
/**
 * AppUserMenu
 *
 * The avatar + name + dropdown ("My Profile" / "Sign Out") from the dashboard
 * header — extracted so chrome-free pages (settings welcome/stepper) can
 * reuse just this part without the rest of AppHeader (sidebar toggles,
 * search, notifications).
 */
import Menu from 'primevue/menu';
import { useAuthStore } from '@/stores';
import { useUserDisplayName } from '@/modules/auth/composables/useUserDisplayName';
import { useTranslation } from 'i18next-vue';

const props = defineProps<{
  /** Settings-onboarding in progress: only Sign Out is offered, no My Profile link. */
  hideProfileLink?: boolean;
}>();

const { t } = useTranslation();
const authStore = useAuthStore();
const router = useRouter();

const userDisplayName = useUserDisplayName();

const userInitials = computed(() => {
  const name = userDisplayName.value;
  if (!name.trim()) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
});

const userMenu = ref();
const userMenuItems = computed(() => {
  const items = [];
  if (!props.hideProfileLink) {
    items.push({
      label: t('profile.pageTitle'),
      icon: 'pi pi-user',
      command: () => router.push({ name: 'profile' }),
    });
  }
  items.push({
    label: t('auth.logout'),
    icon: 'pi pi-sign-out',
    command: () => authStore.logout(),
  });
  return items;
});

function toggleUserMenu(event: Event) {
  userMenu.value.toggle(event);
}
</script>

<template>
  <button
    v-if="userDisplayName"
    type="button"
    class="flex items-center gap-x-2.5 rounded-lg px-2.5 py-1.5 cursor-pointer hover:bg-gray-100 transition-colors focus:outline-none"
    aria-haspopup="true"
    @click="toggleUserMenu"
  >
    <span class="flex items-center justify-center h-7 w-7 rounded-full bg-blue-600 text-white text-xs font-semibold shadow-sm">
      {{ userInitials }}
    </span>
    <span class="hidden lg:block text-sm font-semibold text-gray-900">
      {{ userDisplayName }}
    </span>
    <i class="pi pi-chevron-down hidden lg:block text-xs text-gray-400" />
  </button>
  <Menu
    ref="userMenu"
    :model="userMenuItems"
    :popup="true"
  />
</template>
